import {
  X,
  ExternalLink,
  MousePointer,
  Send,
  TrendingUp,
  Eye,
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function NotificationDetailModal({
  isOpen,
  onClose,
  notification,
}) {
  if (!isOpen || !notification) return null;
  const isScheduled = "schedule" in notification;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-card rounded-2xl shadow-xl w-full max-w-3xl border border-border my-8 overflow-y-auto h-[90%]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">
            Notification Details
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-secondary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Title</p>
                  <p className="font-medium text-foreground">
                    {notification.title}
                  </p>
                </div>
                {/* <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Campaign</p>
                  <p className="font-medium text-foreground">
                    {notification.campaign}
                  </p>
                </div> */}
                <div className="p-4 bg-secondary/30 rounded-lg md:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">URL</p>
                  <a
                    href={notification.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline flex items-center gap-2"
                  >
                    {notification.url}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* Images */}
            {(notification.banner || notification.image) && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Media
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {notification.banner && (
                    <div className="p-4 bg-secondary/30 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">
                        Banner
                      </p>
                      <img
                        src={notification.banner}
                        alt="Banner"
                        className="w-full h-32 object-cover rounded"
                      />
                    </div>
                  )}
                  {notification.image && (
                    <div className="p-4 bg-secondary/30 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">
                        Image
                      </p>
                      <img
                        src={notification.image}
                        alt="Notification"
                        className="w-full h-32 object-cover rounded"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* UTM Parameters */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                UTM Parameters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    UTM Source
                  </p>
                  <p className="font-medium text-foreground font-mono">
                    {notification.utm_source}
                  </p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    UTM Medium
                  </p>
                  <p className="font-medium text-foreground font-mono">
                    {notification.utm_medium}
                  </p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">
                    UTM Campaign
                  </p>
                  <p className="font-medium text-foreground font-mono">
                    {notification.utm_campaign}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {notification.actionButtons &&
              notification.actionButtons.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Action Buttons
                  </h3>
                  <div className="space-y-3">
                    {notification.actionButtons.map((button, index) => (
                      <div
                        key={index}
                        className="p-4 bg-secondary/30 rounded-lg flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium text-foreground">
                            {button.title}
                          </p>
                          <a
                            href={button.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            {button.url}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Performance Stats (for sent notifications) */}
            {!isScheduled && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Performance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Send className="h-5 w-5 text-green-600" />
                      <p className="text-sm text-muted-foreground">
                        Total Sent
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {notification.totalUserSend?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MousePointer className="h-5 w-5 text-purple-600" />
                      <p className="text-sm text-muted-foreground">
                         Clicked
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">
                      {notification.clicked?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-orange-600" />
                      <p className="text-sm text-muted-foreground">CTR</p>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">
                      {notification.ctr || "0%"}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-5 w-5 text-blue-600" />
                      <p className="text-sm text-muted-foreground">
                        {" "}
                        Total received
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">
                      {notification.immpression || "0"}
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <X className="h-5 w-5 text-red-600" />
                      <p className="text-sm text-muted-foreground">
                        {" "}
                        Total closed
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-red-600">
                      {notification.closeCount || "0"}
                    </p>
                  </div>
                  {/* <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MousePointer className="h-5 w-5 text-purple-600" />
                      <p className="text-sm text-muted-foreground">
                        Total Close count
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">
                      {notification.closeCount?.toLocaleString() || 0}
                    </p>
                  </div> */}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                  {notification.btn_list.map((el, index) => {
                    return (
                      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <MousePointer className="h-5 w-5 text-purple-600" />
                          <p className="text-sm text-muted-foreground">
                            Button {index + 1} clicked
                          </p>
                        </div>
                        {el.action == "btn2" && (
                          <p className="text-2xl font-bold text-purple-600">
                            {notification.bt2Clount?.toLocaleString() || 0}
                          </p>
                        )}
                        {el.action == "btn1" && (
                          <p className="text-2xl font-bold text-purple-600">
                            {notification.bt1Clount?.toLocaleString() || 0}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Sent On</p>
                  <p className="font-medium text-foreground">
                    {notification.date}
                  </p>
                </div>
                {notification.completedAt && (
                  <div className="mt-4 p-4 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">
                      completed On
                    </p>
                    <p className="font-medium text-foreground">
                      {notification.completedAt}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Schedule Info (for scheduled notifications) */}
            {isScheduled && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Schedule Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">
                      Schedule
                    </p>
                    <p className="font-medium text-foreground">
                      {notification.schedule}
                    </p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">
                      Date Range
                    </p>
                    <p className="font-medium text-foreground">
                      {notification.dateRange}
                    </p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Status</p>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        notification.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {notification.status}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
